import {
  memo,
  Dispatch,
  FormEvent,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
} from 'react';

import { Field } from '@atlaskit/form';
import InfoIcon from '@atlaskit/icon/glyph/info';
import Select, { OptionType, ValueType } from '@atlaskit/select';
import Textfield from '@atlaskit/textfield';

import { PipelineVariablesValues } from '../types';

import {
  VariableDescriptionWrapper,
  VariableKey,
  VariablesDescription,
  VariablesKeyColumn,
  VariablesWrapper,
} from './styled';

type Variable = {
  name: string;
  default?: string;
  allowedValues?: string[];
  description?: string;
};

type Props = {
  variables: Variable[];
  onChange: Dispatch<SetStateAction<PipelineVariablesValues>>;
};

type VariableValueProps = {
  variable: Variable;
  onChange: Dispatch<SetStateAction<PipelineVariablesValues>>;
};

type VariableDescriptionProps = {
  variableDescription?: string;
};

const VariableDescription = ({
  variableDescription,
}: VariableDescriptionProps) => {
  return variableDescription ? (
    <VariableDescriptionWrapper>
      <InfoIcon label="Info" size="small" />
      <VariablesDescription>{variableDescription}</VariablesDescription>
    </VariableDescriptionWrapper>
  ) : null;
};

const TextVariableValue = ({ variable, onChange }: VariableProps) => {
  const onInputChange = useCallback(
    (e: FormEvent<HTMLInputElement>) => {
      onChange(e.currentTarget.value);
    },
    [onChange]
  );

  return (
    <Field name={variable.name} key={variable.name}>
      {({ fieldProps }) => (
        <Textfield
          defaultValue={variable.default || ''}
          placeholder="value"
          autoFocus
          {...fieldProps}
          onChange={onInputChange}
        />
      )}
    </Field>
  );
};

type SelectVariableOption = ValueType<OptionType & { value: string }>;

const SelectVariableValue = ({ variable, onChange }: VariableProps) => {
  const getSelectOptions = useCallback(() => {
    const list = variable.allowedValues!;
    const defaultValue = variable.default || list[0];
    return {
      options: list.map(value => ({ value, label: value })),
      defaultValue: { value: defaultValue, label: defaultValue },
      onChange: (option: SelectVariableOption) => onChange(option?.value),
    };
  }, [variable, onChange]);

  return (
    <Field<SelectVariableOption> name={variable.name} key={variable.name}>
      {({ fieldProps }) => (
        <Select {...fieldProps} {...getSelectOptions()} placeholder="value" />
      )}
    </Field>
  );
};

const VariableValue = ({ variable, onChange }: VariableValueProps) => {
  const onVariableChange = useCallback(
    (value: string | undefined) => {
      onChange(prevState => ({
        ...prevState,
        [variable.name]: {
          key: variable.name,
          value,
          default: variable.default,
          secured: false,
        },
      }));
    },
    [variable, onChange]
  );

  const VariableComponent = useMemo(
    () =>
      !variable.allowedValues || !variable.allowedValues.length
        ? TextVariableValue
        : SelectVariableValue,
    [variable]
  );

  return <VariableComponent variable={variable} onChange={onVariableChange} />;
};

type VariableProps = {
  variable: Variable;
  onChange: (value: string | undefined) => void;
};

const PipelineVariables = ({ variables, onChange }: Props) => {
  // reset variables values
  useEffect(() => onChange({}), [variables, onChange]);

  return (
    <VariablesWrapper>
      <h4>Variables</h4>
      <p>Set variable values for this run of your pipeline.</p>
      <table>
        <tbody>
          {variables.map((variable: Variable, index) => (
            <tr key={`variable_row_${index}`}>
              <VariablesKeyColumn>
                <VariableKey title={variable.name}>{variable.name}</VariableKey>
              </VariablesKeyColumn>
              <td>
                <VariableValue
                  variable={variable}
                  onChange={onChange}
                ></VariableValue>
                <VariableDescription
                  variableDescription={variable.description}
                ></VariableDescription>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </VariablesWrapper>
  );
};

export default memo(PipelineVariables);
